/* Mckwurst Designs section behavior: hero slideshow and reviews carousel.
   No dependencies. Every control starts hidden in the markup and appears only when this
   script runs, so a page without JavaScript never shows a button that does nothing. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = new WeakMap();

  /* Shared rotation logic. Auto-advance runs only when the section opts in, the visitor has
     not paused it, motion is allowed, and neither the pointer nor focus is inside the root.
     The status region is polite while nothing rotates and silent while it does. */
  function createRotator(root, options) {
    var count = options.count;
    var index = 0;
    var hovered = false;
    var focused = false;
    var userPaused = false;
    var editorHeld = false;
    var timer = null;
    var toggle = options.toggle;
    var status = options.status;
    var template = root.getAttribute('data-status-template') || '';
    var autoplay = root.getAttribute('data-autoplay') === 'true' && count > 1;
    var interval = Number(root.getAttribute('data-interval')) || 7000;

    function rotating() {
      return autoplay && !userPaused && !editorHeld && !reduceMotion.matches;
    }

    function updateLive() {
      if (!status) return;
      status.setAttribute('aria-live', rotating() && !hovered && !focused ? 'off' : 'polite');
    }

    function say() {
      if (!status || !template) return;
      status.textContent = template.replace('__INDEX__', index + 1).replace('__TOTAL__', count);
    }

    function show(next, announce) {
      index = (next + count) % count;
      options.onShow(index);
      if (announce) say();
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      updateLive();
      if (!rotating()) return;
      timer = window.setInterval(function () {
        if (!hovered && !focused) show(index + 1, false);
      }, interval);
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        userPaused = !userPaused;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        start();
      });
    }

    root.addEventListener('mouseenter', function () { hovered = true; updateLive(); });
    root.addEventListener('mouseleave', function () { hovered = false; updateLive(); });
    root.addEventListener('focusin', function () { focused = true; updateLive(); });
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) {
        focused = false;
        updateLive();
      }
    });
    reduceMotion.addEventListener('change', start);

    show(0, false);
    start();

    return {
      go: function (next) { show(next, true); },
      step: function (delta) { show(index + delta, true); },
      /* The theme editor holds the slideshow still while one of its blocks is selected. */
      hold: function (held) { editorHeld = held; start(); },
      destroy: function () {
        stop();
        reduceMotion.removeEventListener('change', start);
      }
    };
  }

  /* Run setup once for each matching root inside scope. A root that is already bound is skipped. */
  function eachRoot(scope, selector, setup) {
    var roots = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) roots.unshift(scope);
    roots.forEach(function (root) {
      if (root.hasAttribute('data-inited')) return;
      var instance = setup(root);
      if (!instance) return;
      root.setAttribute('data-inited', '');
      instances.set(root, instance);
    });
  }

  /* Put a slide into layout so its lazy image starts loading, then call back once the image has
     loaded or failed (at most 4 s) and the slide has a laid-out box for the opacity transition. */
  function whenReady(slide, done) {
    var img = slide.querySelector('img');
    slide.classList.add('is-ready');
    if (img) img.loading = 'eager';
    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      void slide.offsetWidth;
      done();
    }
    if (!img || (img.complete && img.naturalWidth > 0)) { finish(); return; }
    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });
    window.setTimeout(finish, 4000);
  }

  /* Fetch a slide's picture ahead of its first showing, at low priority, so a dot click or the next
     autoplay step finds it cached. The probe copies srcset and sizes, so the browser asks for the same file
     the slide will use. It runs only after the window load event (the first slide, the LCP picture, is done by
     then) and an idle moment, and holds the probes so they are not collected mid-download. */
  var warmProbes = [];

  function afterLoadAndIdle(task) {
    function idle() {
      if ('requestIdleCallback' in window) window.requestIdleCallback(task, { timeout: 3000 });
      else window.setTimeout(task, 1500);
    }
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function warmSlide(slide) {
    var img = slide && slide.querySelector('img');
    if (!img || slide.hasAttribute('data-warmed')) return;
    slide.setAttribute('data-warmed', '');
    var probe = new Image();
    probe.fetchPriority = 'low';
    probe.decoding = 'async';
    probe.sizes = img.getAttribute('sizes') || '100vw';
    if (img.getAttribute('srcset')) probe.srcset = img.getAttribute('srcset');
    probe.src = img.getAttribute('src');
    warmProbes.push(probe);
  }

  /* Hero: crossfade one slide at a time. */
  function initHero(scope) {
    eachRoot(scope, '[data-hero]', function (root) {
      var slides = root.querySelectorAll('[data-hero-slide]');
      var dots = root.querySelectorAll('[data-hero-dot]');
      var controls = root.querySelector('[data-hero-controls]');
      if (slides.length < 2 || !controls) return null;
      var showToken = 0;

      var rotator = createRotator(root, {
        count: slides.length,
        toggle: root.querySelector('[data-hero-toggle]'),
        status: root.querySelector('[data-hero-status]'),
        onShow: function (index) {
          dots.forEach(function (dot, i) { dot.setAttribute('aria-current', i === index ? 'true' : 'false'); });
          var token = ++showToken;
          var next = slides[index];
          afterLoadAndIdle(function () { warmSlide(slides[(index + 1) % slides.length]); });
          /* A slide's image only downloads once the slide is in layout, so wait for it before the fade.
             The token drops a stale request when the visitor clicks again before the image arrives. */
          whenReady(next, function () {
            if (token !== showToken) return;
            slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
          });
        }
      });

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { rotator.go(i); });
      });
      controls.hidden = false;
      return { slides: slides, go: rotator.go, hold: rotator.hold, destroy: rotator.destroy };
    });
  }

  /* Reviews: the active card plus, on wide screens, its two neighbors. The data-pos
     attribute sits on the list item so the layout rules reach the whole card. */
  function initReviews(scope) {
    eachRoot(scope, '[data-reviews]', function (root) {
      var items = root.querySelectorAll('[data-review]');
      var dots = root.querySelectorAll('[data-reviews-dot]');
      var controls = root.querySelector('[data-reviews-controls]');
      var count = items.length;
      if (count < 2 || !controls) return null;

      var rotator = createRotator(root, {
        count: count,
        toggle: root.querySelector('[data-reviews-toggle]'),
        status: root.querySelector('[data-reviews-status]'),
        onShow: function (index) {
          items.forEach(function (item, i) {
            var pos = 'other';
            if (i === index) pos = 'active';
            else if (count > 2 && i === (index + 1) % count) pos = 'next';
            else if (count > 2 && i === (index - 1 + count) % count) pos = 'prev';
            item.parentNode.setAttribute('data-pos', pos);
          });
          dots.forEach(function (dot, i) { dot.setAttribute('aria-current', i === index ? 'true' : 'false'); });
        }
      });

      root.querySelector('[data-reviews-prev]').addEventListener('click', function () { rotator.step(-1); });
      root.querySelector('[data-reviews-next]').addEventListener('click', function () { rotator.step(1); });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { rotator.go(i); });
      });
      controls.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { rotator.step(-1); event.preventDefault(); }
        else if (event.key === 'ArrowRight') { rotator.step(1); event.preventDefault(); }
      });
      controls.hidden = false;
      return { destroy: rotator.destroy };
    });
  }

  function init(scope) {
    initHero(scope);
    initReviews(scope);
  }

  function teardown(scope) {
    scope.querySelectorAll('[data-inited]').forEach(function (root) {
      var instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
      root.removeAttribute('data-inited');
    });
  }

  function heroForBlock(event) {
    var slide = event.target;
    var root = slide.closest ? slide.closest('[data-hero]') : null;
    var instance = root ? instances.get(root) : null;
    if (!instance) return null;
    return { instance: instance, index: Array.prototype.indexOf.call(instance.slides, slide) };
  }

  /* Theme editor: rebuild a section after it reloads, release it when it goes away, and hold
     the hero on the slide whose block is selected. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { teardown(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var hit = heroForBlock(event);
    if (!hit) return;
    if (hit.index > -1) hit.instance.go(hit.index);
    hit.instance.hold(true);
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var hit = heroForBlock(event);
    if (hit) hit.instance.hold(false);
  });

  init(document);
})();
